import React, { useCallback, useEffect, useState } from 'react';
import {
  Spin,
  message,
  Select,
  Tabs,
  Rate,
  Input,
  Tag,
  Tooltip,
  Empty,
} from 'antd';
import {
  GoogleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RobotOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { TWButton } from '../../../components/ui';
import { TableRowsSkeleton } from '../../../components/skeleton/skeleton';
import googleBusinessApi from '../../../services/googleBusinessApi';
import storeApi from '../../../services/storeApi';
import ModalConfirmation from '../components/ModalConfirmation';

const { Option } = Select;
const { TextArea } = Input;

const unwrap = (response) => response?.data?.data;

// ---------------------------------------------------------------------------
// Header (connect / disconnect)
// ---------------------------------------------------------------------------
export const GoogleBusinessIntegrationHeader = ({ globalCompany }) => {
  const [connected, setConnected] = useState(null);
  const [checking, setChecking] = useState(false);
  const [hoveringDisconnect, setHoveringDisconnect] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState(false);

  const checkConnection = useCallback(async () => {
    if (!globalCompany?.id) return;
    setChecking(true);
    try {
      const status = unwrap(await googleBusinessApi.getStatus(globalCompany.id));
      setConnected(Boolean(status?.connected));
    } catch (error) {
      setConnected(false);
    } finally {
      setChecking(false);
    }
  }, [globalCompany?.id]);

  useEffect(() => {
    checkConnection();
  }, [checkConnection]);

  // Google Business connect is not finished yet — re-enable when it is.
  // const connect = () => {
  //   if (!process.env.REACT_APP_GOOGLE_BUSINESS_CLIENT_ID) {
  //     message.error('Google Business client id is not configured.');
  //     return;
  //   }
  //   window.location.href = googleBusinessApi.buildConnectUrl(globalCompany.id);
  // };

  const handleDisconnect = async () => {
    setDisconnectModal(false);
    setChecking(true);
    try {
      await googleBusinessApi.disconnect({ companyId: globalCompany.id });
      message.success('Google Business Profile disconnected');
      setConnected(false);
    } catch (error) {
      message.error(
        'Failed to disconnect: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setChecking(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <span className="pl-5 text-[28px] text-[#4285F4]">
            <GoogleOutlined />
          </span>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-slate-400">
              Google Business Profile
            </div>
          </div>
        </div>
        <div className="pr-5">
          {checking ? (
            <Spin />
          ) : connected !== null ? (
            connected ? (
              <Tooltip title="Disconnect">
                <div
                  className="connection-icon-wrapper"
                  onMouseEnter={() => setHoveringDisconnect(true)}
                  onMouseLeave={() => setHoveringDisconnect(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDisconnectModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {hoveringDisconnect ? (
                    <CloseCircleFilled
                      style={{ fontSize: '32px', color: '#ff4d4f' }}
                    />
                  ) : (
                    <CheckCircleFilled
                      style={{ fontSize: '32px', color: '#52c41a' }}
                    />
                  )}
                </div>
              </Tooltip>
            ) : (
              <TWButton
                variant="primary"
                label="Connect"
                disabled
                onClick={(e) => {
                  e.stopPropagation();
                  // connect();
                }}
              />
            )
          ) : null}
        </div>
      </div>
      <ModalConfirmation
        show={disconnectModal}
        onClose={() => setDisconnectModal(false)}
        onSubmit={handleDisconnect}
        title="Confirm Disconnection"
        description="Are you sure you want to disconnect your Google Business Profile? Thisway will no longer be able to manage your Google listing."
        buttonText="Disconnect"
        cancelText="Cancel"
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Location selector
// ---------------------------------------------------------------------------
const LocationSelector = ({ globalCompany, onSelected, currentLocation }) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedStore, setSelectedStore] = useState(
    currentLocation?.storeId || undefined
  );
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(!currentLocation);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [locationsRes, storesRes] = await Promise.all([
        googleBusinessApi.getLocations(globalCompany.id),
        storeApi.list(`companyid=${globalCompany.id}`),
      ]);
      setLocations(unwrap(locationsRes) || []);
      setStores(storesRes?.data?.data || []);
    } catch (error) {
      message.error(
        'Failed to load Google locations: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [globalCompany.id]);

  useEffect(() => {
    if (expanded) {
      loadData();
    }
  }, [expanded, loadData]);

  const save = async () => {
    const location = locations.find((l) => l.locationId === selectedLocation);
    if (!location) {
      message.error('Please select a Google location.');
      return;
    }
    setSaving(true);
    try {
      await googleBusinessApi.selectLocation({
        companyId: globalCompany.id,
        accountId: location.accountId,
        locationId: location.locationId,
        storeId: selectedStore || null,
      });
      message.success('Google location saved.');
      setExpanded(false);
      onSelected();
    } catch (error) {
      message.error(
        'Failed to save location: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  if (!expanded && currentLocation) {
    return (
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-b pb-4 mb-4">
        <div>
          <div className="text-sm font-medium text-gray-700 dark:text-slate-300">
            {currentLocation.locationName || 'Selected Google location'}
          </div>
          <div className="text-xs text-gray-500 dark:text-slate-400">
            {currentLocation.address}
          </div>
          {currentLocation.isVerified ? (
            <Tag color="green" className="mt-1 w-fit">
              Verified
            </Tag>
          ) : (
            <Tag color="orange" className="mt-1 w-fit">
              Not verified
            </Tag>
          )}
        </div>
        <TWButton
          variant="subtle"
          size="sm"
          label="Change location"
          onClick={() => setExpanded(true)}
        />
      </div>
    );
  }

  return (
    <div className="border-b pb-4 mb-4">
      <div className="mb-3">
        <h3 className="text-md font-medium text-gray-700 dark:text-slate-300">
          Select your Google Business location
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400">
          Pick the Google location to manage and optionally map it to a Thisway
          store.
        </p>
      </div>
      {loading ? (
        <TableRowsSkeleton count={2} rowHeight="h-12" />
      ) : locations.length === 0 ? (
        <Empty description="No Google locations found for this account." />
      ) : (
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="mb-1 block text-xs text-gray-500 dark:text-slate-400">
              Google location
            </label>
            <Select
              className="w-full"
              placeholder="Select Google location"
              value={selectedLocation}
              onChange={setSelectedLocation}
            >
              {locations.map((loc) => (
                <Option key={loc.locationId} value={loc.locationId}>
                  {loc.title || loc.locationId}
                  {loc.address ? ` - ${loc.address}` : ''}
                </Option>
              ))}
            </Select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs text-gray-500 dark:text-slate-400">
              Thisway store (optional)
            </label>
            <Select
              className="w-full"
              placeholder="Map to a store"
              allowClear
              value={selectedStore}
              onChange={setSelectedStore}
            >
              {stores.map((store) => (
                <Option key={store.id} value={store.id}>
                  {store.name}
                </Option>
              ))}
            </Select>
          </div>
          <TWButton
            variant="primary"
            label="Save"
            loading={saving}
            disabled={!selectedLocation}
            onClick={save}
          />
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Website tab (MVP1)
// ---------------------------------------------------------------------------
const WebsiteTab = ({ globalCompany }) => {
  const [loading, setLoading] = useState(false);
  const [website, setWebsite] = useState(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [thiswayUrl, setThiswayUrl] = useState('');
  const [updating, setUpdating] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = unwrap(await googleBusinessApi.getWebsite(globalCompany.id));
      setWebsite(data);
      const computed = data?.storeSlug
        ? `${process.env.REACT_APP_ORDER_URL}/${data.storeSlug}`
        : '';
      setThiswayUrl(computed);
      setTargetUrl(computed || data?.websiteUrl || '');
    } catch (error) {
      message.error(
        'Failed to load website info: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [globalCompany.id]);

  useEffect(() => {
    load();
  }, [load]);

  const doUpdate = async () => {
    setConfirmModal(false);
    setUpdating(true);
    try {
      const res = unwrap(
        await googleBusinessApi.updateWebsite({
          companyId: globalCompany.id,
          websiteUrl: targetUrl,
        })
      );
      if (res?.succeeded) {
        message.success('Google website link updated.');
        load();
      } else {
        message.error(res?.message || 'Failed to update website link.');
      }
    } catch (error) {
      message.error(
        'Failed to update website: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setUpdating(false);
    }
  };

  const doRollback = async () => {
    setUpdating(true);
    try {
      const res = unwrap(
        await googleBusinessApi.rollbackWebsite({ companyId: globalCompany.id })
      );
      if (res?.succeeded) {
        message.success('Rolled back to the previous website link.');
        load();
      } else {
        message.error(res?.message || 'Nothing to roll back.');
      }
    } catch (error) {
      message.error(
        'Failed to roll back: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <TableRowsSkeleton count={3} rowHeight="h-12" />;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        This will update your public Google Business Profile.
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs text-gray-500 dark:text-slate-400">
            Current Google website URL
          </label>
          <div className="break-all text-sm text-gray-800 dark:text-slate-200">
            {website?.websiteUrl || (
              <span className="text-gray-400">Not set</span>
            )}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-500 dark:text-slate-400">
            Thisway website URL
          </label>
          <div className="break-all text-sm text-gray-800 dark:text-slate-200">
            {thiswayUrl || (
              <span className="text-gray-400">
                Map a store with a slug to compute this
              </span>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs text-gray-500 dark:text-slate-400">
          New website URL to publish
        </label>
        <Input
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
          placeholder="https://..."
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <TWButton
          variant="primary"
          label="Update Google Website Link"
          loading={updating}
          disabled={!targetUrl}
          onClick={() => setConfirmModal(true)}
        />
        <TWButton
          variant="subtle"
          icon={UndoOutlined}
          label="Rollback"
          loading={updating}
          onClick={doRollback}
        />
      </div>

      <ModalConfirmation
        show={confirmModal}
        onClose={() => setConfirmModal(false)}
        onSubmit={doUpdate}
        title="Update public Google listing?"
        description="This will update your public Google Business Profile website link. Do you want to continue?"
        buttonText="Update"
        cancelText="Cancel"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Reviews tab (MVP2)
// ---------------------------------------------------------------------------
const ReviewsTab = ({ globalCompany }) => {
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = unwrap(await googleBusinessApi.getReviews(globalCompany.id));
      setReviews(data || []);
    } catch (error) {
      message.error(
        'Failed to load reviews: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [globalCompany.id]);

  useEffect(() => {
    load();
  }, [load]);

  const setReviewBusy = (reviewId, key, value) =>
    setBusy((prev) => ({ ...prev, [`${reviewId}:${key}`]: value }));

  const draftWithAi = async (reviewId) => {
    setReviewBusy(reviewId, 'draft', true);
    try {
      const draft = unwrap(
        await googleBusinessApi.draftReply({
          companyId: globalCompany.id,
          reviewId,
        })
      );
      setDrafts((prev) => ({ ...prev, [reviewId]: draft || '' }));
    } catch (error) {
      message.error(
        'Failed to draft reply: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setReviewBusy(reviewId, 'draft', false);
    }
  };

  const sendReply = async (reviewId) => {
    const comment = drafts[reviewId];
    if (!comment || !comment.trim()) {
      message.error('Reply cannot be empty.');
      return;
    }
    setReviewBusy(reviewId, 'send', true);
    try {
      await googleBusinessApi.replyToReview({
        companyId: globalCompany.id,
        reviewId,
        comment,
      });
      message.success('Reply sent to Google.');
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[reviewId];
        return next;
      });
      load();
    } catch (error) {
      message.error(
        'Failed to send reply: ' +
          (error.response?.data?.Message || error.message)
      );
    } finally {
      setReviewBusy(reviewId, 'send', false);
    }
  };

  if (loading) {
    return <TableRowsSkeleton count={3} rowHeight="h-16" />;
  }

  if (reviews.length === 0) {
    return <Empty description="No Google reviews yet." />;
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => {
        const draft = drafts[review.reviewId];
        return (
          <div
            key={review.reviewId}
            className="rounded-md border p-4 dark:border-slate-700"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800 dark:text-slate-200">
                  {review.reviewerName || 'Anonymous'}
                </span>
                <Rate disabled value={review.starRating} />
              </div>
              {review.createTime && (
                <span className="text-xs text-gray-400">
                  {new Date(review.createTime).toLocaleDateString()}
                </span>
              )}
            </div>

            {review.comment && (
              <p className="mt-2 text-sm text-gray-600 dark:text-slate-300">
                {review.comment}
              </p>
            )}

            {review.hasReply ? (
              <div className="mt-3 rounded bg-slate-50 p-3 dark:bg-slate-700/40">
                <div className="text-xs font-medium text-gray-500 dark:text-slate-400">
                  Your reply
                </div>
                <p className="text-sm text-gray-700 dark:text-slate-200">
                  {review.replyComment}
                </p>
              </div>
            ) : (
              <div className="mt-3">
                {draft !== undefined ? (
                  <div className="space-y-2">
                    <TextArea
                      rows={3}
                      value={draft}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [review.reviewId]: e.target.value,
                        }))
                      }
                    />
                    <div className="flex flex-wrap gap-2">
                      <TWButton
                        variant="primary"
                        size="sm"
                        label="Approve & Send"
                        loading={busy[`${review.reviewId}:send`]}
                        onClick={() => sendReply(review.reviewId)}
                      />
                      <TWButton
                        variant="subtle"
                        size="sm"
                        icon={RobotOutlined}
                        label="Redraft"
                        loading={busy[`${review.reviewId}:draft`]}
                        onClick={() => draftWithAi(review.reviewId)}
                      />
                      <TWButton
                        variant="ghost"
                        size="sm"
                        label="Cancel"
                        onClick={() =>
                          setDrafts((prev) => {
                            const next = { ...prev };
                            delete next[review.reviewId];
                            return next;
                          })
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <TWButton
                    variant="subtle"
                    size="sm"
                    icon={RobotOutlined}
                    label="Reply with AI"
                    loading={busy[`${review.reviewId}:draft`]}
                    onClick={() => draftWithAi(review.reviewId)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------
const GoogleBusinessIntegration = ({ globalCompany }) => {
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!globalCompany?.id) return;
    setChecking(true);
    try {
      const data = unwrap(await googleBusinessApi.getStatus(globalCompany.id));
      setStatus(data);
    } catch (error) {
      setStatus({ connected: false });
    } finally {
      setChecking(false);
    }
  }, [globalCompany?.id]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  if (checking) {
    return (
      <div className="pl-5 py-4 w-full">
        <TableRowsSkeleton count={3} rowHeight="h-12" />
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="pl-5 py-4 text-sm text-gray-500 dark:text-slate-400">
        Connect to Google Business Profile using the button in the header to
        manage your listing.
      </div>
    );
  }

  const hasLocation = Boolean(status?.location?.locationId);

  const tabs = [
    {
      key: 'website',
      label: 'Website',
      children: <WebsiteTab globalCompany={globalCompany} />,
    },
    {
      key: 'reviews',
      label: 'Reviews',
      children: <ReviewsTab globalCompany={globalCompany} />,
    },
  ];

  return (
    <div className="pl-5 pr-5 pt-4">
      <LocationSelector
        globalCompany={globalCompany}
        currentLocation={status?.location}
        onSelected={loadStatus}
      />
      {hasLocation ? (
        <Tabs defaultActiveKey="website" items={tabs} />
      ) : (
        <div className="text-sm text-gray-500 dark:text-slate-400">
          Select a Google location above to manage your website link and
          reviews.
        </div>
      )}
    </div>
  );
};

export default GoogleBusinessIntegration;
